import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, ShoppingCart, ShoppingBag, Package, Users, Wallet, FileText,
  Factory, Store, BarChart3, Settings, LogOut, ChevronDown, Sparkles,
  X as XIcon, Shield, Monitor, KeyRound, Link2, Truck, Wrench, Workflow, Home,
  PanelLeftClose, PanelLeft, Headphones, Snowflake, HelpCircle, FileSignature,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuth, cn } from '@/lib/auth'
import { useFilial } from '@/lib/filial'
import { useFilialLogo } from '@/lib/useFilialLogo'
import { api } from '@/lib/api'

export interface NavItem {
  to: string; label: string; icon: any; badge?: string;
  badgeCount?: number;
  children?: { to: string; label: string; badgeCount?: number }[];
  moduleKey?: string;
  superOnly?: boolean;
  adminOnly?: boolean;
}

// Empresas habilitadas pro módulo Grades (borracha/calçados). Mantém em sync com
// EMPRESAS_COM_BORRACHA do backend (cadastros/grade_routes.py).
const EMPRESAS_COM_GRADES = new Set([11])

export function getNav(isSuperAdmin: boolean, modules: Record<string, boolean> | null, opsPropostas = 0, empresaId: number | null = null, decisoesPendentes = 0, creditoPendentes = 0): NavItem[] {
  const m = modules || {}
  const items: NavItem[] = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  ]

  if (isSuperAdmin) {
    items.push({
      to: '/superadmin', label: 'Super Admin', icon: Shield, superOnly: true,
      children: [
        { to: '/superadmin', label: 'Dashboard Global' },
        { to: '/superadmin/empresas', label: 'Empresas' },
        { to: '/superadmin/grupos', label: 'Grupos / Intercompany' },
        { to: '/superadmin/billing', label: 'Billing / Assinaturas' },
      ],
    })
  }

  items.push({ to: '/pdv', label: 'PDV-Loja', icon: Monitor, badge: 'F6' })
  // FEATURE 30/06: PDV-online (caixa separado). ESCONDIDO por padrão — opt-in via
  // permissão do perfil (/pdv-online). O filtro abaixo (filtrarPorPermissoes) trata
  // a exceção: só aparece com isAdmin OU permissoes['/pdv-online'] === true.
  items.push({ to: '/pdv-online', label: 'PDV-Online', icon: Monitor, badge: 'F7' })

  items.push({
    to: '/vendas', label: 'Vendas', icon: ShoppingCart, moduleKey: 'vendas',
    badgeCount: decisoesPendentes,  // mostra no grupo Vendas qdo há decisão pendente
    children: [
      { to: '/vendas', label: 'Pedidos' },
      { to: '/vendas/novo', label: 'Novo Pedido' },
      { to: '/vendas/trocas', label: 'Trocas de Produtos' },
      { to: '/vendas/decisoes-qualidade', label: 'Decisões de Qualidade', badgeCount: decisoesPendentes },
      // Layout do Pedido escondido por ora (a pedido do cliente) — rota ainda existe
      // Itens de Mercado Livre só quando o módulo marketplace está ativo
      ...(m['marketplace'] !== false ? [
        { to: '/vendas/importar-ml', label: 'Importar Pedido ML' },
        { to: '/vendas/metricas', label: 'Métricas (ML)' },
        { to: '/vendas/ao-vivo', label: 'Vendas Ao Vivo (ML)' },
      ] : []),
    ],
  })
  items.push({
    to: '/orcamentos', label: 'Orçamentos', icon: FileSignature,
    children: [
      { to: '/orcamentos', label: 'Todos os orçamentos' },
      { to: '/orcamentos/novo', label: 'Novo Orçamento' },
    ],
  })
  if (m['servicos'] !== false) items.push({
    to: '/servicos', label: 'Serviços (OS)', icon: Wrench, moduleKey: 'servicos',
    children: [
      { to: '/servicos', label: 'Ordens de Serviço' },
      { to: '/servicos/novo', label: 'Nova OS' },
    ],
  })
  if (m['comodato'] !== false) items.push({
    to: '/comodato', label: 'Comodato', icon: Snowflake, moduleKey: 'comodato',
  })
  if (m['rh'] !== false) items.push({
    to: '/rh', label: 'RH · Pessoal', icon: Users, moduleKey: 'rh',
  })
  if (m['roteirizacao'] !== false) items.push({
    to: '/roteirizacao', label: 'Roteirização', icon: Sparkles, moduleKey: 'roteirizacao',
  })
  if (m['bpm'] !== false) {
    items.push({
      to: '/bpm', label: 'BPM / Processos', icon: Workflow, moduleKey: 'bpm',
    })
  }
  if (m['intranet'] !== false) {
    items.push({
      to: '/intranet', label: 'Intranet', icon: Home, moduleKey: 'intranet',
    })
  }
  if (m['sac'] !== false) {
    items.push({
      to: '/sac', label: 'SAC', icon: Headphones, moduleKey: 'sac',
    })
  }
  if (m['controladoria'] !== false) {
    items.push({
      to: '/controladoria', label: 'Controladoria', icon: BarChart3, moduleKey: 'controladoria',
    })
  }
  items.push({
    to: '/comercial', label: 'Comercial', icon: ShoppingBag,
    children: [
      { to: '/comercial/venda-geral', label: 'Venda Geral' },
      { to: '/comercial/painel-auditoria', label: 'Painel de Auditoria' },
      { to: '/comercial/rotas', label: 'Rotas de Visita' },
      { to: '/comercial/sugestoes-compra', label: 'Sugestões de Compra' },
      { to: '/compras', label: 'Compras' },
      { to: '/compras/nf-entradas', label: 'NFs de Entrada' },
      { to: '/compras/nfe-a-receber', label: 'NF-e a Receber (SEFAZ)' },
      { to: '/compras/intercompany', label: 'Comprar do grupo' },
      // Marketplace só quando o módulo está ativo
      ...(m['marketplace'] !== false ? [
        { to: '/marketplace', label: 'Marketplace' },
        { to: '/marketplace/resumo-financeiro-ml', label: 'Resumo Financeiro ML' },
      ] : []),
      { to: '/comercial/metas', label: 'Metas' },
      { to: '/comercial/comissao-vendedor', label: 'Comissão de Vendedor' },
      { to: '/comercial/relatorios-gerenciais', label: 'Relatórios Gerenciais' },
      { to: '/relatorios/vendedores', label: 'Vendas por Vendedor' },
      { to: '/relatorios/curva-abc', label: 'Curva ABC' },
    ],
  })
  items.push({
    to: '/expedicao', label: 'Expedição', icon: Truck,
    children: [
      { to: '/expedicao/cargas', label: 'Cargas de Distribuição' },
      { to: '/expedicao/mapa', label: 'Mapa de Cargas' },
      { to: '/expedicao/mdfe', label: 'MDF-e' },
      { to: '/expedicao/veiculos', label: 'Veículos' },
      { to: '/expedicao/motoristas', label: 'Motoristas' },
      { to: '/expedicao/rotas', label: 'Rotas de Frete' },
      { to: '/expedicao/layout-mapa', label: 'Layout do Mapa' },
      { to: '/expedicao/layout-romaneio', label: 'Layout do Romaneio' },
    ],
  })
  const cadastrosChildren = [
    { to: '/cadastros/clientes', label: 'Clientes' },
    { to: '/cadastros/fornecedores', label: 'Fornecedores' },
    { to: '/cadastros/produtos', label: 'Produtos' },
    { to: '/cadastros/tabelas-preco', label: 'Tabelas de Preço' },
    { to: '/cadastros/campanhas-desconto', label: 'Campanha de Descontos' },
    { to: '/cadastros/categorias', label: 'Grupos e Subgrupos' },
    { to: '/cadastros/vendedores', label: 'Vendedores' },
    { to: '/cadastros/tecnicos', label: 'Técnicos/Mecânicos' },
    { to: '/cadastros/equipamentos', label: 'Equipamentos/Bikes' },
    { to: '/cadastros/motivos-devolucao', label: 'Motivos de Devolução' },
    { to: '/cadastros/regras-imposto', label: 'Regra de Imposto' },
  ]
  if (empresaId && EMPRESAS_COM_GRADES.has(empresaId)) {
    cadastrosChildren.push({ to: '/cadastros/grades', label: 'Grades' })
  }
  items.push({
    to: '/cadastros', label: 'Cadastros', icon: Users,
    children: cadastrosChildren,
  })
  if (m['estoque'] !== false) items.push({
    to: '/estoque', label: 'Estoque', icon: Package, moduleKey: 'estoque',
    children: [
      { to: '/estoque', label: 'Movimentos' },
      { to: '/estoque/entrada', label: 'Entrada (NF)' },
      { to: '/estoque/ajuste', label: 'Ajuste de estoque' },
      { to: '/estoque/transferencias', label: 'Transferências' },
      { to: '/estoque/inventarios', label: 'Inventário' },
      { to: '/relatorios/estoque', label: 'Posição de Estoque' },
      { to: '/relatorios/produtos-categoria', label: 'Produtos por Categoria' },
    ]
  })
  if (m['financeiro'] !== false) items.push({
    to: '/financeiro', label: 'Financeiro', icon: Wallet, moduleKey: 'financeiro',
    badgeCount: creditoPendentes,
    children: [
      { to: '/financeiro/venda-recebimentos', label: 'Venda por Recebimentos' },
      { to: '/financeiro/receber', label: 'Contas a Receber' },
      { to: '/financeiro/pagar', label: 'Contas a Pagar' },
      { to: '/financeiro/creditos', label: 'Crédito de Clientes' },
      { to: '/financeiro/cheques', label: 'Carteira de Cheques' },
      { to: '/financeiro/correcao-forma', label: 'Corrigir Forma de Recebimento' },
      { to: '/expedicao/cargas', label: 'Acerto de Cargas' },
      { to: '/relatorios/devolucoes', label: 'Relatório de Devoluções' },
      { to: '/relatorios/creditos-cliente', label: 'Relatório de Crédito' },
      { to: '/financeiro/borderos', label: 'Borderôs de Pagamento' },
      { to: '/financeiro/analise-credito', label: 'Análise de Crédito', badgeCount: creditoPendentes },
      { to: '/financeiro/contas-bancarias', label: 'Contas Bancárias' },
      { to: '/financeiro/formas-pagamento', label: 'Formas de Pagamento' },
      { to: '/financeiro/boletos', label: 'Emissão de Boletos' },
      { to: '/financeiro/retornos', label: 'Retornos Bancários' },
      { to: '/financeiro/pagamentos-lote', label: 'Pagamentos em Lote' },
      { to: '/financeiro/config-boleto', label: 'Configuração de Boleto' },
      { to: '/financeiro/lancamentos', label: 'Lançamentos' },
      { to: '/financeiro/tesouraria', label: 'Tesouraria' },
      { to: '/financeiro/conciliacao', label: 'Conciliação Bancária' },
      { to: '/financeiro/plano-contas', label: 'Plano de Contas' },
      { to: '/financeiro/fluxo-caixa', label: 'Fluxo de Caixa' },
      { to: '/financeiro/dre', label: 'DRE' },
      { to: '/caixa/config-formas', label: 'Config. Formas Pgto' },
      { to: '/caixa/sessoes', label: 'Fechamento de Caixa' },
    ],
  })
  if (m['fiscal'] !== false) items.push({
    to: '/fiscal', label: 'Fiscal', icon: FileText, moduleKey: 'fiscal',
    children: [
      { to: '/fiscal', label: 'Documentos Fiscais' },
      { to: '/fiscal/tributacao-produtos', label: 'Tributação por Produto' },
      // [PORT-MAURITI 2026-08-11] CBS/IBS/IS — LC 214/2025. Parametrização, simulador e
      // apuração. Emissão dos grupos no XML fica atrás do gate `emitir_no_xml`.
      { to: '/fiscal/reforma', label: 'Reforma (CBS/IBS/IS)' },
      { to: '/fiscal/cfop', label: 'CFOP' },
      { to: '/fiscal/mensagens', label: 'Mensagens da Nota' },
      { to: '/fiscal/livros', label: 'Livros Fiscais' },
      { to: '/nfe-propria', label: 'NFe Própria' },
      { to: '/apuracao-difal', label: 'Apuração DIFAL' },
    ],
  })
  if (m['industrial'] !== false) items.push({
    to: '/industrial', label: 'Indústria', icon: Factory, moduleKey: 'industrial',
    badgeCount: opsPropostas,
    children: [
      { to: '/industrial/dashboard', label: 'Dashboard' },
      { to: '/industrial/indicadores', label: 'OEE · ETA · Capacidade' },
      { to: '/industrial/turnos', label: 'Turnos' },
      { to: '/industrial/sos', label: 'Andon · SOS' },
      { to: '/industrial/andon', label: 'Painel TV (Andon)' },
      { to: '/industrial/ordens', label: 'Ordens de Produção', badgeCount: opsPropostas },
      { to: '/industrial/carga', label: 'Gerar Carga (PCP)' },
      { to: '/industrial/lotes', label: 'Lotes / Rastreabilidade' },
      { to: '/industrial/manutencao', label: 'Manutenção' },
      { to: '/industrial/fichas', label: 'Fichas Técnicas' },
      { to: '/industrial/materias-primas', label: 'Matérias-primas' },
      { to: '/industrial/maquinas', label: 'Máquinas' },
      { to: '/industrial/moldes', label: 'Moldes' },
      { to: '/industrial/etapas', label: 'Fluxo de Etapas' },
    ],
  })
  // Marketplace movido pra dentro de Comercial
  // Relatórios eliminado: Visão do Dono → Controladoria, Fluxo Caixa+DRE → Financeiro,
  // Posição Estoque + Produtos por Categoria → Estoque

  items.push({
    to: '/configuracoes', label: 'Configurações', icon: Settings,
    adminOnly: true,
    children: [
      { to: '/configuracoes', label: 'Geral' },
      { to: '/configuracoes/empresa', label: 'Empresa' },
      { to: '/configuracoes/filiais', label: 'Filiais' },
      { to: '/configuracoes/usuarios', label: 'Usuários' },
      { to: '/configuracoes/perfis', label: 'Perfis de Acesso' },
      { to: '/configuracoes/depositos', label: 'Depósitos' },
      ...(m['ia'] !== false ? [{ to: '/configuracoes/ia', label: 'Sofia' }] : []),
      ...(m['loja'] !== false ? [{ to: '/configuracoes/loja', label: 'Minha Loja Online' }] : []),
      { to: '/configuracoes/serasa', label: 'Integração Serasa' },
    ],
  })

  items.push({ to: '/ajuda', label: 'Ajuda', icon: HelpCircle })

  return items
}


/**
 * Filtra o menu pelas permissões do perfil do usuário.
 * - admin / sem permissões definidas → vê tudo (permissoes = null libera geral).
 * - cada item/filho é escondido quando permissoes[to] === false.
 * - grupo some quando todos os filhos foram bloqueados.
 */
export function filtrarPorPermissoes(
  nav: NavItem[],
  permissoes: Record<string, boolean> | null | undefined,
  isAdmin: boolean,
): NavItem[] {
  if (isAdmin || !permissoes) return nav
  // FEATURE 30/06: rotas OPT-IN ficam escondidas por padrão (só aparecem com
  // permissoes[to] === true). Ex.: /pdv-online (caixa separado). As demais seguem
  // a regra normal (aparecem a menos que permissoes[to] === false).
  const OPT_IN = new Set<string>(['/pdv-online'])
  const liberado = (to: string) => OPT_IN.has(to) ? (permissoes[to] === true) : (permissoes[to] !== false)
  const out: NavItem[] = []
  for (const item of nav) {
    if (item.superOnly) { out.push(item); continue }  // seção super admin não é filtrada aqui
    if (item.children?.length) {
      const filhos = item.children.filter(c => liberado(c.to))
      if (filhos.length === 0) continue  // grupo inteiro bloqueado
      out.push({ ...item, children: filhos })
    } else if (liberado(item.to)) {
      out.push(item)
    }
  }
  return out
}


/**
 * FEATURE 01/07: esconde do menu itens configurados globalmente na empresa
 * (Configurações > Menu → empresa.menu_oculto). É um filtro GLOBAL (vale pra todos,
 * inclusive admin), separado de filtrarPorPermissoes (que é por perfil).
 * - remove qualquer item/filho cuja rota esteja em `ocultos`.
 * - grupo some quando todos os filhos ficam ocultos.
 * A rota continua existindo (acessível por URL) — só some do menu.
 */
export function filtrarOcultos(nav: NavItem[], ocultos: string[] | null | undefined): NavItem[] {
  if (!ocultos || ocultos.length === 0) return nav
  const set = new Set(ocultos)
  const out: NavItem[] = []
  for (const item of nav) {
    if (item.children?.length) {
      // Grupos: só filtramos os FILHOS. Não removemos pelo `to` do pai porque ele
      // costuma coincidir com o `to` de um filho (ex.: grupo Vendas e o filho
      // "Pedidos" são ambos /vendas) — remover pelo pai apagaria o grupo inteiro
      // ao esconder só um filho. Se todos os filhos somem, o grupo some junto.
      const filhos = item.children.filter(c => !set.has(c.to))
      if (filhos.length === 0) continue
      out.push({ ...item, children: filhos })
    } else {
      if (set.has(item.to)) continue  // item avulso (PDV, Comodato, etc.) oculto
      out.push(item)
    }
  }
  return out
}


/* ============================================================
   Modal: Trocar minha senha
============================================================ */
interface ChangePasswordModalProps {
  open: boolean
  onClose: () => void
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const [senhaAtual, setSenhaAtual] = useState('')
  const [senhaNova, setSenhaNova] = useState('')
  const [senhaConfirma, setSenhaConfirma] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setSenhaAtual(''); setSenhaNova(''); setSenhaConfirma(''); setLoading(false)
    }
  }, [open])

  if (!open) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (senhaNova.length < 6) {
      toast.error('Nova senha precisa ter no mínimo 6 caracteres')
      return
    }
    if (senhaNova !== senhaConfirma) {
      toast.error('As senhas não conferem')
      return
    }
    setLoading(true)
    try {
      await api.post('/auth/trocar-senha', {
        senha_atual: senhaAtual,
        senha_nova: senhaNova,
      })
      toast.success('Senha alterada com sucesso')
      onClose()
    } catch {
      // interceptor do api.ts já mostra o toast de erro
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-brand-600" />
            <h2 className="font-semibold text-gray-900">Trocar minha senha</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <XIcon size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Senha atual</label>
            <input
              type="password"
              required
              autoFocus
              value={senhaAtual}
              onChange={e => setSenhaAtual(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="Digite sua senha atual"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
            <input
              type="password"
              required
              minLength={6}
              value={senhaNova}
              onChange={e => setSenhaNova(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="Mínimo 6 caracteres"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nova senha</label>
            <input
              type="password"
              required
              minLength={6}
              value={senhaConfirma}
              onChange={e => setSenhaConfirma(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm"
              placeholder="Digite a nova senha novamente"
            />
            {senhaConfirma && senhaNova !== senhaConfirma && (
              <p className="text-xs text-red-600 mt-1">Senhas não conferem</p>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !senhaAtual || !senhaNova || !senhaConfirma}
              className="flex-1 px-4 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
            >
              {loading ? 'Alterando...' : 'Alterar senha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


interface SidebarProps { open: boolean; onClose: () => void; collapsed?: boolean; onToggleCollapse?: () => void }

export default function Sidebar({ open, onClose, collapsed = false, onToggleCollapse }: SidebarProps) {
  const { user, empresa, logout } = useAuth()
  const filialAtual = useFilial(s => s.filialAtual)
  const modoConsolidado = useFilial(s => s.modoConsolidado)
  const filialLogo = useFilialLogo()
  const navigate = useNavigate()
  const location = useLocation()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [changePwOpen, setChangePwOpen] = useState(false)

  // Navegação do menu por teclado (setas) — complementa Tab/Enter nativos (mouse intacto).
  //  ↑/↓ move entre itens visíveis · → expande grupo (ou entra nos filhos) · ← recolhe/volta ao pai
  //  Home/End vão ao primeiro/último. Enter/Espaço já funcionam nativos (âncora navega, grupo expande).
  function onMenuKey(e: React.KeyboardEvent<HTMLElement>) {
    const k = e.key
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(k)) return
    const navEl = e.currentTarget
    const itens = Array.from(navEl.querySelectorAll<HTMLElement>('a[href],button[data-menu-item]'))
      .filter(el => el.offsetParent !== null)  // só os visíveis (filhos recolhidos não estão no DOM)
    if (!itens.length) return
    const atual = document.activeElement as HTMLElement
    const i = itens.indexOf(atual)
    const focar = (el?: HTMLElement) => el?.focus()
    if (k === 'ArrowDown') { e.preventDefault(); focar(itens[i < 0 ? 0 : Math.min(i + 1, itens.length - 1)]) }
    else if (k === 'ArrowUp') { e.preventDefault(); focar(itens[i < 0 ? 0 : Math.max(i - 1, 0)]) }
    else if (k === 'Home') { e.preventDefault(); focar(itens[0]) }
    else if (k === 'End') { e.preventDefault(); focar(itens[itens.length - 1]) }
    else if (k === 'ArrowRight') {
      const grupo = atual?.getAttribute('data-group')
      if (!grupo) return
      e.preventDefault()
      if (!expanded[grupo]) setExpanded(s => ({ ...s, [grupo]: true }))   // recolhido → expande
      else setTimeout(() => focar(navEl.querySelector<HTMLElement>(`[data-parent="${CSS.escape(grupo)}"]`) || undefined), 0)  // já aberto → 1º filho
    }
    else if (k === 'ArrowLeft') {
      const pai = atual?.getAttribute('data-parent')
      const grupo = atual?.getAttribute('data-group')
      if (pai) {  // está num filho → recolhe o pai e volta o foco pro cabeçalho do grupo
        e.preventDefault()
        setExpanded(s => ({ ...s, [pai]: false }))
        setTimeout(() => focar(navEl.querySelector<HTMLElement>(`[data-group="${CSS.escape(pai)}"]`) || undefined), 0)
      } else if (grupo && expanded[grupo]) {  // grupo aberto → recolhe
        e.preventDefault()
        setExpanded(s => ({ ...s, [grupo]: false }))
      }
    }
  }

  const isSuperAdmin = user?.perfil === 'super_admin'
  const isAdmin = isSuperAdmin || user?.nivel === 'admin' || user?.perfil === 'admin'
  const industrialAtivo = (empresa?.modulos_ativos as any)?.industrial !== false
  const { data: opContagem } = useQuery<Record<string, number>>({
    queryKey: ['ops-contagem'],
    queryFn: async () => (await api.get('/industrial/ordens-contagem')).data,
    enabled: !!user && !isSuperAdmin && industrialAtivo,
    refetchInterval: 30000,
  })
  const propostasPendentes = opContagem?.proposta || 0

  // Decisões de qualidade pendentes (badge ao lado do link)
  const { data: qualidadeContagem } = useQuery<{ pendentes: number }>({
    queryKey: ['qualidade-decisoes-contagem'],
    queryFn: async () => (await api.get('/vendas/qualidade/contagem')).data,
    enabled: !!user && !isSuperAdmin,
    refetchInterval: 30000,
  })
  const decisoesPendentes = qualidadeContagem?.pendentes || 0

  // Clientes pendentes de liberação de crédito (cadastros novos sem análise)
  const { data: creditoContagem } = useQuery<{ pendentes: number }>({
    queryKey: ['credito-pendentes-contagem'],
    queryFn: async () => (await api.get('/financeiro/analise-credito/contagem-pendentes')).data,
    enabled: !!user && !isSuperAdmin,
    refetchInterval: 60000,
  })
  const creditoPendentes = creditoContagem?.pendentes || 0

  const NAV_FULL = getNav(isSuperAdmin, empresa?.modulos_ativos ?? null, propostasPendentes, empresa?.id ?? null, decisoesPendentes, creditoPendentes)
  // FEATURE 01/07: 1º filtra por perfil (permissoes), depois esconde os itens
  // marcados como ocultos globalmente na empresa (Configurações > Menu).
  const NAV = filtrarOcultos(filtrarPorPermissoes(NAV_FULL, user?.permissoes, isAdmin), empresa?.menu_oculto)

  useEffect(() => { onClose() }, [location.pathname])

  const handleLogout = () => { logout(); navigate('/login') }

  const sidebar = (
    <aside className={cn(
      "h-full bg-ink-950 text-ink-100 flex flex-col transition-[width] duration-200",
      collapsed ? "w-14" : "w-64"
    )}>
      <div className={cn(
        "h-16 flex items-center border-b border-ink-800 shrink-0",
        collapsed ? "justify-center px-2" : "gap-2.5 px-5"
      )}>
        <img src={filialLogo || "/logo-empresa.jpeg"} alt="Logo" className="w-8 h-8 rounded-md object-cover shrink-0 ring-1 ring-white/10" />
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white leading-none text-sm">VELORE ERP</div>
              <div className="text-[10px] text-ink-400 mt-0.5 tracking-wider truncate">
                {isSuperAdmin
                  ? 'SUPER ADMIN'
                  : modoConsolidado
                    ? 'TODAS AS FILIAIS'
                    : filialAtual?.nome_fantasia || filialAtual?.razao_social || empresa?.nome_fantasia || 'GESTÃO EMPRESARIAL'}
              </div>
            </div>
            <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-ink-800 text-ink-400">
              <XIcon size={18} />
            </button>
            {onToggleCollapse && (
              <button onClick={onToggleCollapse}
                title="Recolher menu"
                className="hidden lg:flex p-1.5 rounded hover:bg-ink-800 text-ink-400 hover:text-white">
                <PanelLeftClose size={16} />
              </button>
            )}
          </>
        )}
      </div>
      {/* Em modo collapsed: botão pra expandir aparece sozinho como banda fina */}
      {collapsed && onToggleCollapse && (
        <button onClick={onToggleCollapse}
          title="Expandir menu"
          className="hidden lg:flex justify-center py-2 border-b border-ink-800 hover:bg-ink-800 text-ink-300 hover:text-white">
          <PanelLeft size={16} />
        </button>
      )}

      <nav onKeyDown={onMenuKey} className={cn("flex-1 overflow-y-auto py-3 space-y-0.5", collapsed ? "px-1.5" : "px-3")}>
        {NAV.filter(item => !item.adminOnly || isAdmin).map((item) => {
          const Icon = item.icon
          const hasChildren = !!item.children?.length
          const isExpanded = expanded[item.to]

          if (!hasChildren) {
            return (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) => cn(
                  'flex items-center rounded-md text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset',
                  collapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2',
                  isActive ? 'bg-brand-600 text-white font-medium' : 'text-ink-300 hover:bg-ink-900 hover:text-white',
                  item.superOnly && !collapsed && 'border-l-2 border-amber-500'
                )}>
                <Icon size={18} />
                {!collapsed && <><span>{item.label}</span>
                  {item.badge && <span className="ml-auto text-[10px] bg-brand-500 text-white px-1.5 py-0.5 rounded">{item.badge}</span>}
                </>}
              </NavLink>
            )
          }
          return (
            <div key={item.to}>
              <button
                data-menu-item data-group={item.to}
                onClick={() => {
                  // Em modo collapsed, clicar navega pro primeiro filho (não tem como expandir inline)
                  if (collapsed && item.children?.length) {
                    navigate(item.children[0].to)
                  } else {
                    setExpanded(s => ({ ...s, [item.to]: !s[item.to] }))
                  }
                }}
                title={collapsed ? item.label : undefined}
                className={cn(
                  'w-full flex items-center rounded-md text-sm text-ink-300 hover:bg-ink-900 hover:text-white transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset',
                  collapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2',
                  item.superOnly && !collapsed && 'border-l-2 border-amber-500'
                )}>
                <Icon size={18} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {!!item.badgeCount && (
                      <span className="mr-1 bg-sky-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                        {item.badgeCount}
                      </span>
                    )}
                    <ChevronDown size={14} className={cn('transition-transform', isExpanded && 'rotate-180')} />
                  </>
                )}
                {collapsed && !!item.badgeCount && (
                  <span className="absolute ml-6 -mt-4 bg-sky-500 text-white text-[9px] font-semibold px-1 rounded-full min-w-[14px] text-center">
                    {item.badgeCount}
                  </span>
                )}
              </button>
              {!collapsed && isExpanded && (
                <div className="ml-8 mt-0.5 space-y-0.5 border-l border-ink-800 pl-3">
                  {item.children!.map(ch => (
                    <NavLink key={ch.to} to={ch.to} data-parent={item.to}
                      className={({ isActive }) => cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded text-xs transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset',
                        isActive ? 'text-brand-300 font-medium' : 'text-ink-400 hover:text-white'
                      )}>
                      <span className="flex-1">{ch.label}</span>
                      {!!ch.badgeCount && (
                        <span className="bg-sky-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                          {ch.badgeCount}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {empresa && !collapsed && (
        <div className="px-4 py-2 border-t border-ink-800">
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <span className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase',
              empresa.plano === 'enterprise' ? 'bg-amber-600 text-white' :
              empresa.plano === 'pro' ? 'bg-brand-600 text-white' :
              empresa.plano === 'starter' ? 'bg-emerald-600 text-white' : 'bg-ink-700 text-ink-300'
            )}>
              {empresa.plano}
            </span>
          </div>
        </div>
      )}

      <div className={cn("border-t border-ink-800 shrink-0", collapsed ? "p-1.5" : "p-3")}>
        <div className={cn("flex items-center", collapsed ? "flex-col gap-1.5" : "gap-2 px-2 py-2")}>
          <div className="w-8 h-8 rounded-full bg-brand-700 flex items-center justify-center text-white text-sm font-medium shrink-0"
            title={collapsed ? user?.nome : undefined}>
            {user?.nome.charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{user?.nome}</div>
            <div className="text-xs text-ink-400 truncate capitalize">{user?.perfil.replace('_', ' ')}</div>
          </div>
          )}
          <button
            onClick={() => setChangePwOpen(true)}
            title="Trocar minha senha"
            className="p-1.5 rounded hover:bg-ink-900 text-ink-400 hover:text-brand-400 transition-colors"
          >
            <KeyRound size={16} />
          </button>
          <button onClick={handleLogout} title="Sair"
            className="p-1.5 rounded hover:bg-ink-900 text-ink-400 hover:text-red-400 transition-colors">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  )

  return (
    <>
      {/* Desktop */}
      {/* h-screen (100vh) estourava sob a propriedade CSS `zoom` (virava 100vh*zoom).
          Compensa com /var(--zoom) pra a barra acompanhar o zoom. (fix 26/06) */}
      <div className="hidden lg:flex h-[calc(100vh/var(--zoom,1))] sticky top-0 shrink-0">{sidebar}</div>
      {/* Mobile overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />
          <div className="relative h-full animate-slide-in">{sidebar}</div>
        </div>
      )}
      {/* Modal sempre no final */}
      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </>
  )
}
